import { Component, computed, inject, signal } from '@angular/core';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatDividerModule } from '@angular/material/divider';
import { RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { navigationData, NavItem } from './nav.data';
import { SidebarService } from '../../services/sidebar.service';
import { ThemeService } from '../../services/theme.service';
import { UserService } from '../../app/core/services/user.service';
import { RoleType } from '../../app/core/services/role.service';
import { RoleTransformPipe } from '../../app/shared/pipes/role-transform.pipe';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    MatSidenavModule,
    MatListModule,
    MatIconModule,
    MatButtonModule,
    MatExpansionModule,
    MatDividerModule,
    RouterModule,
    CommonModule,
    RoleTransformPipe,
  ],
  template: `
    <div
      class="sidebar-content"
      (mouseenter)="onSidebarHover()"
      (mouseleave)="onSidebarLeave()"
    >
      <!-- Logo Section -->
      <div class="logo-section">
        <div class="logo-container">
          <img
            *ngIf="shouldShowExpandedContent()"
            [src]="logoFull()"
            alt="Admin Panel"
            class="logo-full"
          />
          <img
            *ngIf="!shouldShowExpandedContent()"
            [src]="logoMini()"
            alt="Admin Panel"
            class="logo-mini"
          />
        </div>
      </div>

      <!-- Navigation Menu -->
      <nav class="nav-menu">
        <mat-nav-list>
          <ng-container *ngFor="let item of navigationItems()">
            <div *ngIf="!item.children; else hasChildren">
              <mat-list-item
                [routerLink]="item.route"
                routerLinkActive="active-link"
                class="nav-item"
              >
                <mat-icon matListItemIcon>{{ item.icon }}</mat-icon>
                <span
                  matListItemTitle
                  [class.hidden]="!shouldShowExpandedContent()"
                  >{{ item.label }}</span
                >
              </mat-list-item>
            </div>

            <ng-template #hasChildren>
              <div
                *ngIf="!shouldShowExpandedContent(); else expandedMenu"
                class="collapsed-menu-item"
              >
                <mat-list-item
                  class="nav-item collapsed-parent"
                  [title]="item.label"
                >
                  <mat-icon matListItemIcon>{{ item.icon }}</mat-icon>
                </mat-list-item>
              </div>

              <ng-template #expandedMenu>
                <mat-expansion-panel class="nav-expansion-panel">
                  <mat-expansion-panel-header class="nav-expansion-header">
                    <mat-panel-title class="nav-panel-title">
                      <mat-icon class="nav-expansion-icon">{{
                        item.icon
                      }}</mat-icon>
                      <span>{{ item.label }}</span>
                    </mat-panel-title>
                  </mat-expansion-panel-header>

                  <div class="nav-children">
                    <mat-nav-list>
                      <mat-list-item
                        *ngFor="let child of item.children"
                        [routerLink]="child.route"
                        routerLinkActive="active-link"
                        class="nav-child-item"
                      >
                        <mat-icon matListItemIcon>{{ child.icon }}</mat-icon>
                        <span matListItemTitle>{{ child.label }}</span>
                      </mat-list-item>
                    </mat-nav-list>
                  </div>
                </mat-expansion-panel>
              </ng-template>
            </ng-template>
          </ng-container>
        </mat-nav-list>
      </nav>

      <!-- User Card Section -->
      <div class="user-card-section" *ngIf="userService.user$ | async as user">
        <div class="user-card">
          <div class="user-avatar">
            <span class="user-initials">{{
              getUserInitials(user.fullName)
            }}</span>
          </div>
          <div class="user-info" [class.hidden]="!shouldShowExpandedContent()">
            <p class="user-name">{{ user.fullName }}</p>
            <p class="user-role">{{ user.role.name | roleTransform }}</p>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .sidebar-content {
        height: 100%;
        overflow-y: auto;
        background-color: var(--color-surface);
        transition: width 0.3s ease;
        width: 100%;
      }

      .hidden {
        opacity: 0;
        visibility: hidden;
        transition:
          opacity 0.2s ease,
          visibility 0.2s ease;
      }

      .nav-item span {
        transition:
          opacity 0.3s ease,
          visibility 0.3s ease;
      }

      .logo-section {
        display: flex;
        align-items: center;
        padding: 16px;
        height: 80px;
        box-sizing: border-box;
        background-color: var(--color-surface);
      }

      .logo-container {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        overflow: hidden;
        cursor: pointer;
        border-radius: var(--border-radius-sm);
        transition: background-color 0.3s ease;
      }

      .logo-container:hover {
        background-color: rgba(var(--color-primary), 0.05);
      }

      .logo-full {
        height: 48px;
        width: auto;
        max-width: 200px;
        transition:
          opacity 0.3s ease,
          transform 0.3s ease;
      }

      .logo-full:hover {
        transform: scale(1.02);
      }

      .logo-mini {
        height: 36px;
        width: 36px;
        transition:
          opacity 0.3s ease,
          transform 0.3s ease;
      }

      .logo-mini:hover {
        transform: scale(1.1);
      }

      .nav-menu {
        padding: 8px;
      }

      .nav-item {
        margin-bottom: 4px;
        border-radius: var(--border-radius-sm);
        transition: background-color 0.3s ease;
        overflow: hidden;
        color: var(--text-primary);
      }

      .nav-item:hover {
        background-color: var(--color-primary-light) !important;
        color: var(--text-on-primary) !important;
      }

      .nav-item:hover mat-icon {
        color: var(--text-on-primary) !important;
      }

      .active-link {
        background-color: var(--color-primary) !important;
        color: var(--text-on-primary) !important;
      }

      .active-link mat-icon {
        color: var(--text-on-primary) !important;
      }

      .active-link span {
        color: var(--text-on-primary) !important;
      }

      .nav-child-item.active-link {
        background-color: var(--color-primary) !important;
        color: var(--text-on-primary) !important;
      }

      .nav-child-item.active-link span {
        color: var(--text-on-primary) !important;
      }

      .nav-expansion-panel {
        box-shadow: none !important;
        background: transparent;
        margin-bottom: 4px;
      }

      .nav-expansion-header {
        padding: 0 16px;
        border-radius: var(--border-radius-sm);
        transition: background-color 0.3s ease;
        color: var(--text-primary);
      }

      .nav-expansion-header:hover {
        background-color: var(--color-primary-light) !important;
        color: var(--text-on-primary) !important;
      }

      .nav-expansion-header:hover mat-icon {
        color: var(--text-on-primary) !important;
      }

      .nav-panel-title {
        display: flex;
        align-items: center;
        gap: 12px;
        overflow: hidden;
      }

      .nav-expansion-icon {
        margin-right: 0;
        flex-shrink: 0;
      }

      .nav-children {
        padding-left: 16px;
      }

      .nav-child-item {
        margin-bottom: 2px;
        border-radius: var(--border-radius-sm);
        font-size: 0.9rem;
        color: var(--text-secondary);
      }

      .nav-child-item:hover {
        background-color: var(--color-primary-light);
        color: var(--text-on-primary);
      }

      .nav-child-item:hover mat-icon {
        color: var(--text-on-primary);
      }

      .collapsed-menu-item {
        margin-bottom: 4px;
      }

      .collapsed-parent {
        border-radius: var(--border-radius-sm);
        transition: background-color 0.3s ease;
        padding: 0 !important;
        color: var(--text-primary);
      }

      .collapsed-parent:hover {
        background-color: var(--color-primary-light);
        color: var(--text-on-primary);
      }

      .collapsed-parent:hover mat-icon {
        color: var(--text-on-primary);
      }

      .collapsed-parent .mat-mdc-list-item-content {
        justify-content: center !important;
        padding: 0 !important;
      }

      .collapsed-parent .mat-mdc-list-item-content .mat-list-item-icon {
        margin-right: 0 !important;
        margin-left: 0 !important;
      }

      .collapsed-parent .mdc-list-item__primary-text {
        display: none;
      }

      /* Icon-only mode styles for regular nav items */
      :host-context(.mat-sidenav[style*='72px']) .nav-item {
        padding: 0;
        justify-content: center;
      }

      :host-context(.mat-sidenav[style*='72px'])
        .nav-item
        .mat-mdc-list-item-content {
        justify-content: center;
      }

      /* Ensure collapsed parent items have same styling as regular items */
      .collapsed-parent {
        height: 48px;
        min-height: 48px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .collapsed-parent .mat-list-item-icon {
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
      }

      /* Scrollbar styling */
      .sidebar-content::-webkit-scrollbar {
        width: 4px;
      }

      .sidebar-content::-webkit-scrollbar-track {
        background: transparent;
      }

      .sidebar-content::-webkit-scrollbar-thumb {
        background: var(--text-secondary);
        border-radius: 2px;
        opacity: 0.3;
      }

      .sidebar-content::-webkit-scrollbar-thumb:hover {
        opacity: 0.5;
      }

      @media (max-width: 1024px) {
        .logo-section {
          padding: 12px;
          height: 72px;
        }

        .nav-menu {
          padding: 4px;
        }
      }

      /* User Card Styles */
      .sidebar-content {
        display: flex;
        flex-direction: column;
      }

      .nav-menu {
        flex: 1;
        overflow-y: auto;
      }

      .user-card-section {
        padding: 12px 8px;
        border-top: 1px solid var(--bs-border-color);
        background-color: var(--color-surface);
        margin-top: auto;
      }

      .user-card {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px;
        border-radius: var(--border-radius-sm);
        background-color: color-mix(
          in srgb,
          var(--color-primary) 5%,
          transparent
        );
        transition: all 0.3s ease;
        cursor: pointer;
      }

      .user-card:hover {
        background-color: color-mix(
          in srgb,
          var(--color-primary) 10%,
          transparent
        );
      }

      .user-avatar {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 40px;
        border-radius: 50%;
        background-color: var(--color-primary);
        flex-shrink: 0;
      }

      .user-avatar mat-icon {
        color: var(--text-on-primary);
        font-size: 24px;
        width: 24px;
        height: 24px;
      }

      .user-initials {
        color: var(--text-on-primary);
        font-size: 14px;
        font-weight: 700;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
      }

      .user-info {
        flex: 1;
        overflow: hidden;
      }

      .user-name {
        margin: 0;
        font-size: 13px;
        font-weight: 600;
        color: var(--text-primary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .user-role {
        margin: 2px 0 0 0;
        font-size: 11px;
        color: var(--text-secondary);
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
    `,
  ],
})
export class SidebarComponent {
  private themeService = inject(ThemeService);
  userService = inject(UserService);

  constructor(private sidebarService: SidebarService) {}

  // Convert user$ to signal to use in computed
  private userSignal = toSignal(this.userService.user$, { initialValue: null });

  // Computed signal for filtered navigation items
  navigationItems = computed(() => {
    const user = this.userSignal();
    if (!user) return [];

    return this.filterNavItems(navigationData, user);
  });

  // Helper function to filter NavItems recursively
  private filterNavItems(items: NavItem[], user: any): NavItem[] {
    return items
      .map((item) => {
        // If the item has defined roles, check if the user has them
        if (item.roles && item.roles.length > 0) {
          const hasRole = item.roles.includes(user.role.name as RoleType);
          if (!hasRole) return null;
        }

        // If it has children, filter them recursively
        if (item.children) {
          const filteredChildren = this.filterNavItems(item.children, user);
          if (
            filteredChildren.length === 0 &&
            item.roles &&
            item.roles.length > 0
          ) {
            // If there are no children after filtering and the item requires roles, don't show the parent item
            return null;
          }
          return { ...item, children: filteredChildren };
        }

        return item;
      })
      .filter((item) => item !== null) as NavItem[];
  }

  isCollapsed = computed(() => this.sidebarService.isCollapsed());
  shouldShowExpandedContent = computed(() =>
    this.sidebarService.shouldShowExpandedContent(),
  );

  // Computed for full logo according to theme
  logoFull = computed(() => {
    return this.themeService.currentTheme() === 'dark'
      ? 'logo-dark 18@2x.png'
      : 'logo-light 18@2x-100.jpg';
  });

  // Computed for mini logo according to theme
  logoMini = computed(() => {
    return this.themeService.currentTheme() === 'dark'
      ? 'mini-logo-dark 16@2x.png'
      : 'mini-logo-light 16@2x-100.jpg';
  });

  onSidebarHover() {
    this.sidebarService.onSidebarHover();
  }

  onSidebarLeave() {
    this.sidebarService.onSidebarLeave();
  }

  getUserInitials(fullName: string): string {
    const parts = fullName
      .trim()
      .split(' ')
      .filter((part) => part.length > 0);
    if (parts.length === 0) return '';
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    // First letter of first name + First letter of last name
    return (
      parts[0].charAt(0) + parts[parts.length - 1].charAt(0)
    ).toUpperCase();
  }
}
